import { Button, Chip, IconButton, Spinner } from '@material-tailwind/react';
import { ContactIcon, /* DownloadIcon, */ EyeIcon, PencilIcon, PlusIcon, RefreshCwIcon, SnowflakeIcon, TrashIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useGetContractsByUserId } from '../../../custom-hooks/use-contracts';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { useDeleteBooking } from '../../../custom-hooks/use-bookings';
import BookingFreezeModal from '../../booking/bookingFreezeModal';
import BookingFreezeManageModal from '../../booking/bookingFreezeModal/manage';
import { InterpretActiveStatus } from '../../../utils/ui-interpreters';
import { convertToPresentableText } from '../../../utils/functions';
import moment from 'moment';
import { VERIFICATION_TYPES } from '../../../utils/enums';
import BookingFormModal from '../../booking/bookingFormModal';
import ContractFormModal from '../../contract/contractFormModal';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../custom-hooks/use-permissions';

const INITIAL_BOOKING_FORM_DATA = {
    // Basic Information
    user: '',
    contractType: 'permanent',
    startDate: '',
    endDate: '',
    securityAmount: 0,
};

const INITIAL_CONTRACT_FORM_DATA = {
    // Basic Information
    user: '',
    contractType: 'permanent',
    startDate: '',
    endDate: '',
    status: VERIFICATION_TYPES.APPROVED,
    comment: '',
    reason: '',
    duration: 0,
    noticePeriod: 4,
    selectedOffices: [],
    selectedSeats: [],
    securityDeposit: 0,
    oneTimeServiceCharges: 0,
    oneTimeOtherCharges: [],
};

const toDateInputValue = (value) => (value ? moment(value).format('YYYY-MM-DD') : '');

const mapContractToFormData = (contract) => {
    const selectedOffices = (contract?.bookings || [])
        .filter((booking) => [VERIFICATION_TYPES.APPROVED, VERIFICATION_TYPES.PENDING, VERIFICATION_TYPES.NOTIFIED].includes(booking?.status))
        .filter((booking) => booking?.office)
        .map((booking) => ({
            id: booking.office?._id,
            name: `Office # ${booking.office?.number || 'N/A'} (${booking.office?.code || 'N/A'})`,
            isFullTime: !(booking.dailyStartTime && booking.dailyEndTime),
            startTime: booking.dailyStartTime ? moment(booking.dailyStartTime).format('HH:mm') : '09:00',
            endTime: booking.dailyEndTime ? moment(booking.dailyEndTime).format('HH:mm') : '18:00',
            monthlyRent: booking.rentTotal || 0,
            advanceAmount: Number(booking.advanceAmount || 0),
            securityAmount: Number(booking.securityAmount || 0),
            startDate: toDateInputValue(booking.startDate),
            endDate: toDateInputValue(booking.endDate),
            rate: booking.rentRate || booking.rentTotal || 0,
            comment: booking.comment || '',
        }));

    const selectedSeats = (contract?.bookings || [])
        .filter((booking) => [VERIFICATION_TYPES.APPROVED, VERIFICATION_TYPES.PENDING, VERIFICATION_TYPES.NOTIFIED].includes(booking?.status))
        .filter((booking) => booking?.seat)
        .map((booking) => ({
            id: booking.seat?._id,
            name: `Seat # ${booking.seat?.seatCode || 'N/A'} (${booking.seat?.seatType || 'N/A'})`,
            isFullTime: !(booking.dailyStartTime && booking.dailyEndTime),
            startTime: booking.dailyStartTime ? moment(booking.dailyStartTime).format('HH:mm') : '09:00',
            endTime: booking.dailyEndTime ? moment(booking.dailyEndTime).format('HH:mm') : '18:00',
            monthlyRent: booking.rentTotal || 0,
            advanceAmount: Number(booking.advanceAmount || 0),
            securityAmount: Number(booking.securityAmount || 0),
            startDate: toDateInputValue(booking.startDate),
            endDate: toDateInputValue(booking.endDate),
            rate: booking.rentRate || booking.rentTotal || 0,
            comment: booking.comment || '',
        }));

    return {
        ...INITIAL_CONTRACT_FORM_DATA,
        user: contract?.user || '',
        contractType: contract?.contractType || INITIAL_CONTRACT_FORM_DATA.contractType,
        startDate: toDateInputValue(contract?.startDate),
        endDate: toDateInputValue(contract?.endDate),
        status: contract?.status || INITIAL_CONTRACT_FORM_DATA.status,
        comment: contract?.comment || '',
        reason: contract?.reason || '',
        duration: Number(contract?.duration || 0),
        noticePeriod: Number(contract?.noticePeriod || INITIAL_CONTRACT_FORM_DATA.noticePeriod),
        selectedOffices,
        selectedSeats,
        securityDeposit: Number(contract?.securityDeposit || 0),
        oneTimeServiceCharges: Number(contract?.oneTimeServiceCharges || 0),
        oneTimeOtherCharges: contract?.oneTimeOtherCharges || [],
    };
};

const mapContractToRenewalData = (contract) => {
    const newStart = moment().startOf('day');

    // Rebuild bookings with new dates, using ALL bookings (including completed ones)
    // Deduplicate by office/seat ID — keep only the most recent booking (latest startDate)
    const deduplicateByIdLatest = (bookings) => {
        const map = new Map();
        for (const booking of bookings) {
            const existing = map.get(booking.id);
            if (!existing || moment(booking._originalStartDate).isAfter(moment(existing._originalStartDate))) {
                map.set(booking.id, booking);
            }
        }
        return [...map.values()].map(({ _originalStartDate, ...rest }) => rest);
    };

    const allOfficeBookings = deduplicateByIdLatest(
        (contract?.bookings || [])
            .filter((b) => b?.office)
            .map((booking) => ({
                id: booking.office?._id,
                name: `Office # ${booking.office?.number || 'N/A'} (${booking.office?.code || 'N/A'})`,
                isFullTime: !(booking.dailyStartTime && booking.dailyEndTime),
                startTime: booking.dailyStartTime ? moment(booking.dailyStartTime).format('HH:mm') : '09:00',
                endTime: booking.dailyEndTime ? moment(booking.dailyEndTime).format('HH:mm') : '18:00',
                monthlyRent: booking.rentTotal || 0,
                // Stays empty, same as a freshly-added row — the modal fills a suggestion
                // itself the first time this field is focused (see contractFormModal).
                firstMonthRent: '',
                firstMonthRentTouched: false,
                advanceAmount: Number(booking.advanceAmount || 0),
                securityAmount: Number(booking.securityAmount || 0),
                startDate: newStart.format('YYYY-MM-DD'),
                endDate: '',
                rate: booking.rentRate || booking.rentTotal || 0,
                comment: '',
                _originalStartDate: booking.startDate, // used for dedup, stripped after
            }))
    );

    const allSeatBookings = deduplicateByIdLatest(
        (contract?.bookings || [])
            .filter((b) => b?.seat)
            .map((booking) => ({
                id: booking.seat?._id,
                name: `Seat # ${booking.seat?.seatCode || 'N/A'} (${booking.seat?.seatType || 'N/A'})`,
                isFullTime: !(booking.dailyStartTime && booking.dailyEndTime),
                startTime: booking.dailyStartTime ? moment(booking.dailyStartTime).format('HH:mm') : '09:00',
                endTime: booking.dailyEndTime ? moment(booking.dailyEndTime).format('HH:mm') : '18:00',
                monthlyRent: booking.rentTotal || 0,
                // Stays empty, same as a freshly-added row — the modal fills a suggestion
                // itself the first time this field is focused (see contractFormModal).
                firstMonthRent: '',
                firstMonthRentTouched: false,
                advanceAmount: Number(booking.advanceAmount || 0),
                securityAmount: Number(booking.securityAmount || 0),
                startDate: newStart.format('YYYY-MM-DD'),
                endDate: '',
                rate: booking.rentRate || booking.rentTotal || 0,
                comment: '',
                _originalStartDate: booking.startDate, // used for dedup, stripped after
            }))
    );

    return {
        ...INITIAL_CONTRACT_FORM_DATA,
        isRenewal: true,
        user: contract?.user || '',
        contractType: contract?.contractType || 'permanent',
        startDate: newStart.format('YYYY-MM-DD'),
        endDate: '',
        status: VERIFICATION_TYPES.APPROVED,
        comment: `Renewed from contract ${contract._id}`,
        reason: contract?.reason || '',
        duration: Number(contract?.duration || 0),
        noticePeriod: Number(contract?.noticePeriod || 4),
        selectedOffices: allOfficeBookings,
        selectedSeats: allSeatBookings,
        securityDeposit: Number(contract?.securityDeposit || 0),
        oneTimeServiceCharges: Number(contract?.oneTimeServiceCharges || 0),
        oneTimeOtherCharges: [], // One-time charges don't repeat
    };
};

const Contracts = ({ customer }) => {
    const { can } = usePermissions();
    const [isContractFormModalOpen, setIsContractFormModalOpen] = useState(false);
    const [isUpdatingContract, setIsUpdatingContract] = useState(false);
    const [updatingContractId, setUpdatingContractId] = useState(null);
    const [contractFormData, setContractFormData] = useState(INITIAL_CONTRACT_FORM_DATA);
    const [bookingIdToPerformAction, setBookingIdToPerformAction] = useState(null);
    const [bookingDataForModal, setBookingDataForModal] = useState(INITIAL_BOOKING_FORM_DATA);
    const [isBookingFormModalOpen, setIsBookingFormModalOpen] = useState(false);
    const [isBookingFreezeModalOpen, setIsBookingFreezeModalOpen] = useState(false);
    const [bookingToFreeze, setBookingToFreeze] = useState(null);
    const [isBookingFreezeManageModalOpen, setIsBookingFreezeManageModalOpen] = useState(false);
    const [bookingToManageFreeze, setBookingToManageFreeze] = useState(null);

    const { confirm, ConfirmationDialog } = useConfirm();
    const navigate = useNavigate();

    const deleteBookingMutation = useDeleteBooking();

    const {
        data: contracts,
        isLoading: isContractsLoading,
        error: contractsError,
        refetch: refetchContracts,
    } = useGetContractsByUserId(customer._id);

    // const handleDownloadContractsPdf = () => {
    //     downloadContractsPdf({
    //         contracts: Array.isArray(contracts) ? contracts : [],
    //         customerName: customer?.name || 'Customer',
    //         customerEmail: customer?.email || '',
    //         reportTitle: 'Customer Contracts and Bookings',
    //         fileName: `customer-contracts-${customer?._id || 'report'}.pdf`,
    //     });
    // };



    const handleAddBookingFormModalOpen = (contract) => {
        setBookingIdToPerformAction(null);
        
        const today = moment().startOf('day');
        const contractStart = moment(contract.startDate).startOf('day');
        const defaultStartDate = contractStart.isAfter(today) ? contract.startDate : new Date();

        setBookingDataForModal({
            ...INITIAL_BOOKING_FORM_DATA,
            startDate: defaultStartDate,
            contractEndDate: contract.endDate,
            contract: contract._id,
            user: customer._id
        });
        setIsBookingFormModalOpen(true);
    }

    const handleDeleteBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: "Delete Booking",
            message: "Are you sure you want to delete this booking?",
            confirmText: "Delete",
            cancelText: "Cancel",
            confirmBtnColor: "red",
        });

        if (confirmation) {
            deleteBookingMutation.mutate(bookingId);
        }
    }

    const handleEditBookingFormModalOpen = (booking, contract) => {
        setBookingIdToPerformAction(booking._id);
        setBookingDataForModal({
            ...booking,
            contractEndDate: contract?.endDate || null,
        });
        setIsBookingFormModalOpen(true);
    }

    const handleBookingFormModalClose = () => {
        setBookingIdToPerformAction(null);
        setBookingDataForModal(INITIAL_BOOKING_FORM_DATA);
        setIsBookingFormModalOpen(false);
    }

    const handleAddContractModalOpen = () => {
        setIsUpdatingContract(false);
        setUpdatingContractId(null);
        setContractFormData(INITIAL_CONTRACT_FORM_DATA);
        setIsContractFormModalOpen(true);
    };

    const handleEditContractModalOpen = (contract) => {
        setIsUpdatingContract(true);
        setUpdatingContractId(contract._id);
        setContractFormData(mapContractToFormData(contract));
        setIsContractFormModalOpen(true);
    };

    const handleFreezeBookingModalOpen = (booking) => {
        setBookingToFreeze(booking);
        setIsBookingFreezeModalOpen(true);
    };

    const handleFreezeBookingModalClose = () => {
        setIsBookingFreezeModalOpen(false);
        setBookingToFreeze(null);
    };

    const handleFreezeManageModalOpen = (booking) => {
        setBookingToManageFreeze(booking);
        setIsBookingFreezeManageModalOpen(true);
    };

    const handleFreezeManageModalClose = () => {
        setIsBookingFreezeManageModalOpen(false);
        setBookingToManageFreeze(null);
    };

    const handleRenewContract = (contract) => {
        setIsUpdatingContract(false);     // This is a CREATE, not an update
        setUpdatingContractId(null);
        setContractFormData(mapContractToRenewalData(contract));
        setIsContractFormModalOpen(true);
    };

    const handleContractModalClose = () => {
        setIsContractFormModalOpen(false);
        setIsUpdatingContract(false);
        setUpdatingContractId(null);
        setContractFormData(INITIAL_CONTRACT_FORM_DATA);
    };

    return (
        <div className="border rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ContactIcon size={16} />
                    Contracts
                </h2>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Temporarily commented out PDF download button */}
                    {/* <Button
                        className="rounded-full border flex items-center gap-2"
                        size="sm"
                        variant="outlined"
                        disabled={!customer._id || isContractsLoading || !contracts?.length}
                        onClick={handleDownloadContractsPdf}
                    >
                        <DownloadIcon size={16} />
                        Download PDF
                    </Button> */}
                    {can('FX12-1', 'create_contract') && (
                    <Button className="rounded-full border flex items-center gap-2" size="sm" disabled={!customer._id || isContractsLoading} variant="gradient" onClick={handleAddContractModalOpen}>
                        <PlusIcon size={16} />
                        Create New Contract
                    </Button>
                    )}
                </div>
            </div>

            {isContractsLoading ? <Spinner /> :
                contractsError ? <div className="text-center py-8 text-red-500">Error loading contracts</div> :
                    (contracts?.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No contracts found for this customer
                        </div>
                    ) : (
                        <>
                            {contracts?.map((contract, index) => (
                                <div key={contract._id} className={` rounded-xl p-4 mb-4 ${getStatusColor(contract.status)}`}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold">Contract #{index + 1}</h3>
                                            <p className="text-xs text-gray-600">ID: {contract._id}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {[VERIFICATION_TYPES.APPROVED, VERIFICATION_TYPES.PENDING, VERIFICATION_TYPES.NOTIFIED].includes(contract.status) && (
                                                <IconButton variant="gradient" color="blue" size="sm" className="rounded-full" onClick={() => handleEditContractModalOpen(contract)}>
                                                    <PencilIcon size={12} />
                                                </IconButton>
                                            )}
                                            {contract.status === VERIFICATION_TYPES.COMPLETED && (
                                                <IconButton variant="gradient" color="green" size="sm" className="rounded-full" onClick={() => handleRenewContract(contract)} title="Renew Contract">
                                                    <RefreshCwIcon size={12} />
                                                </IconButton>
                                            )}
                                            {InterpretActiveStatus(contract.status)}
                                            <Chip color="blue" size="sm" variant="ghost" value={convertToPresentableText(contract.contractType)} />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Start Date</p>
                                            <p className="font-medium">{moment(contract.startDate).format("MMM DD, YYYY")}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Security</p>
                                            <p className="font-medium">{formatCurrency(getContractSecurity(contract))}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Advance</p>
                                            <p className="font-medium">{formatCurrency(getContractAdvance(contract))}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Monthly Rent</p>
                                            <p className="font-medium">{formatCurrency(contract
                                                .bookings
                                                .filter((booking) => booking.status === VERIFICATION_TYPES.APPROVED)
                                                .reduce((acc, booking) => acc + booking.rentTotal, 0))}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">End Date</p>
                                            <p className="font-medium">{contract.endDate ? moment(contract.endDate).format("MMM DD, YYYY") : "Open-ended"}</p>
                                        </div>
                                    </div>

                                    {contract.reason && (
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-600">Purpose</p>
                                            <p>{contract.reason}</p>
                                        </div>
                                    )}

                                    {contract.noticePeriod && (
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-600">Notice Period</p>
                                            <p>{contract.noticePeriod} {parseInt(contract.noticePeriod) === 1 ? 'week' : 'weeks'}</p>
                                        </div>
                                    )}

                                    {/* Bookings Section */}
                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-semibold">Bookings ({contract.bookings.length})</h4>
                                            {
                                                contract.status === VERIFICATION_TYPES.APPROVED && (
                                                    <IconButton className="rounded-full" size="sm" variant="outlined" onClick={() => handleAddBookingFormModalOpen(contract)}>
                                                        <PlusIcon size={12} />
                                                    </IconButton>
                                                )
                                            }
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rent</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advance</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Security</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        {contract.status === VERIFICATION_TYPES.APPROVED && (
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {contract.bookings.map((booking) => (
                                                        <tr key={booking._id} className={booking.freeze?.isFrozen ? 'bg-cyan-50' : ''}>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {booking.office ? "Office" : "Seat"}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.office ?
                                                                        `${booking.office.code} (${booking.office.capacity} seats)` :
                                                                        `${booking.seat.seatCode} (${booking.seat.seatType})`}
                                                                </div>
                                                                {booking.freeze?.isFrozen && (
                                                                    <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-cyan-700">
                                                                        <SnowflakeIcon size={10} /> Frozen {moment(booking.freeze.freezeStartDate).format('MMM D')} – {moment(booking.freeze.freezeEndDate).format('MMM D, YYYY')}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">
                                                                    {booking.office ?
                                                                        `${booking.office.floor.building.name}, Floor ${booking.office.floor.floorCode}` :
                                                                        `${booking.seat.coworking.floor.building.name}, Floor ${booking.seat.coworking.floor.floorCode}`}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.office ?
                                                                        booking.office.floor.building.branch.name :
                                                                        booking.seat.coworking.floor.building.branch.name} Branch
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {booking.dailyStartTime && booking.dailyEndTime ? (
                                                                    <div>
                                                                        <div>Daily: {moment(booking.dailyStartTime).format("h:mm A")} - {moment(booking.dailyEndTime).format("h:mm A")}</div>
                                                                        <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : `${contract.noticePeriod} weeks notice`}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <div>Full-time access</div>
                                                                        <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : "Open"}</div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {formatCurrency(booking.rentTotal)}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.rentFrequency}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {formatCurrency(booking.advanceAmount || 0)}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {formatCurrency(booking.securityAmount || 0)}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${getStatusBadgeColor(booking.status)}`}>
                                                                    {convertToPresentableText(booking.status)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="flex items-center gap-1">

                                                                    <IconButton variant="gradient" color="blue" size="sm" className="rounded-full" onClick={() => navigate(`booking/${booking._id}`)}>
                                                                        <EyeIcon size={12} />
                                                                    </IconButton>
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && booking.status === VERIFICATION_TYPES.APPROVED && !booking.freeze?.isFrozen && (
                                                                        <IconButton variant="gradient" color="cyan" size="sm" className="rounded-full" title="Freeze Booking" onClick={() => handleFreezeBookingModalOpen(booking)}>
                                                                            <SnowflakeIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && booking.freeze?.isFrozen && (
                                                                        <IconButton variant="gradient" color="orange" size="sm" className="rounded-full" title="Manage Freeze" onClick={() => handleFreezeManageModalOpen(booking)}>
                                                                            <SnowflakeIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && [VERIFICATION_TYPES.APPROVED, VERIFICATION_TYPES.PENDING].includes(booking.status) && (
                                                                        <IconButton variant="gradient" color="orange" size="sm" className="rounded-full" onClick={() => handleEditBookingFormModalOpen(booking, contract)}>
                                                                            <PencilIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && [VERIFICATION_TYPES.APPROVED, VERIFICATION_TYPES.PENDING].includes(booking.status) && (
                                                                        <IconButton variant="gradient" color="red" size="sm" className="rounded-full" onClick={() => handleDeleteBooking(booking._id)}>
                                                                            <TrashIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    ))}
            {/* Modals */}
            <ContractFormModal
                isOpen={isContractFormModalOpen}
                onClose={handleContractModalClose}
                customer={customer}
                defaultData={contractFormData}
                isUpdating={isUpdatingContract}
                updatingContractId={updatingContractId}
                onSuccess={() => refetchContracts()}
            />
            {isBookingFormModalOpen && (
                <BookingFormModal
                    isOpen={isBookingFormModalOpen}
                    onClose={handleBookingFormModalClose}
                    defaultData={bookingDataForModal}
                    isUpdating={!!bookingIdToPerformAction}
                    updatingBookingId={bookingIdToPerformAction}
                    customerId={customer._id}
                    onSuccess={() => refetchContracts()}
                />
            )}
            {isBookingFreezeModalOpen && bookingToFreeze && (
                <BookingFreezeModal
                    isOpen={isBookingFreezeModalOpen}
                    onClose={handleFreezeBookingModalClose}
                    booking={bookingToFreeze}
                    onSuccess={() => refetchContracts()}
                />
            )}
            {isBookingFreezeManageModalOpen && bookingToManageFreeze && (
                <BookingFreezeManageModal
                    isOpen={isBookingFreezeManageModalOpen}
                    onClose={handleFreezeManageModalClose}
                    booking={bookingToManageFreeze}
                    onSuccess={() => refetchContracts()}
                />
            )}
            <ConfirmationDialog />
        </div>
    );
};

// Helper functions
function getStatusColor(status) {
    switch (status) {
        case 'approved':
            return 'border-green-200 bg-green-50';
        case 'pending':
            return 'border-yellow-200 bg-yellow-50';
        case 'rejected':
            return 'border-red-200 bg-red-50';
        case 'completed':
            return 'border-gray-300 bg-gray-100';
        case 'cancelled':
            return 'border-gray-200 bg-gray-50';
        default:
            return 'border-gray-200';
    }
}

function getStatusBadgeColor(status) {
    switch (status) {
        case 'approved':
            return 'bg-green-100 text-green-800';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'rejected':
            return 'bg-red-100 text-red-800';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800';
        case 'completed':
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

function getContractSecurity(contract) {
    const bookingSecurity = (contract?.bookings || []).reduce((sum, booking) => sum + Number(booking?.securityAmount || 0), 0);
    if (bookingSecurity > 0) {
        return bookingSecurity;
    }
    return Number(contract?.securityDeposit ?? contract?.totalAdvanceRent ?? 0);
}

function getContractAdvance(contract) {
    const bookingAdvance = (contract?.bookings || []).reduce((sum, booking) => sum + Number(booking?.advanceAmount || 0), 0);
    return bookingAdvance;
}

export default Contracts;